/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import createStyles from "@material-ui/core/styles/createStyles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import classNames from "classnames";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";

// CT Kit
import Button from "components/CustomButtons/Button.jsx";

import { closeButton } from "components/Form/FormField";
import { idModalOK, idModalChk, idModalBtn } from "assets/jss/views/ElementIDs";

import modalStyle from "assets/jss/material-kit-pro-react/modalStyle.jsx";
const styles = createStyles(modalStyle as any);

interface Props extends WithStyles<typeof styles> {
  children?: React.ReactNode;
  title: string; // title of the alert box

  // the body can be either a piece of text ("body") or a ReactNode ("child")
  body?: string;

  color?: string;

  // a single "OK " for default, for more buttons, use "buttons" array
  buttons?: string[];
  // for multiple buttons, "id": "n" means the nth button is clicked, and "text" for optional return
  onButtons?: (id: number, text?: string | null) => void;

  // nobackdrop: true means user must respond to the alert UX for closing
  nobackdrop?: boolean;

  // Alert can take a text input
  label?: string; // for text input
  password?: boolean; // is the input text a password

  // Label for a checkbox for confirming
  labelChk?: string;

  // wider modal
  wide?: boolean;

  // handle routine for Close
  onExit: () => void;
}

const ModalBase: React.FC<Props> = ({
  classes,
  children,
  title,
  body,
  color = "secondary",
  buttons,
  onButtons,
  nobackdrop,
  label,
  password,
  labelChk,
  wide,
  onExit,
}: Props) => {
  const inputTextId = "ac_input_text1";
  const btnList: string[] = buttons || ["OK"];
  const [chkConfirm, setChkConfirm] = useState(false);

  const buttonsRender = btnList.map((button, idx: number) => {
    return (
      <Button
        id={button === "OK" ? idModalOK : `${idModalBtn}${idx}`}
        key={idx}
        style={{ marginLeft: "1rem" }}
        color="success"
        round
        disabled={idx === 0 && labelChk && !chkConfirm}
        onClick={() => {
          if (onButtons) {
            if (label) {
              const input = document.getElementById(
                inputTextId,
              ) as HTMLInputElement;
              onButtons(idx, input ? input.value : null);
            } else {
              onButtons(idx);
            }
          }
          if (onExit !== undefined) {
            onExit();
          }
        }}>
        {button}
      </Button>
    );
  });

  const textInputRender = label ? (
    <div>
      <TextField
        autoFocus
        margin="dense"
        id={inputTextId}
        label={label}
        type={password ? "password" : "email"}
        fullWidth
      />
    </div>
  ) : (
    ""
  );

  const chkboxRender = labelChk && (
    <FormControlLabel
      classes={{ label: classes.alertText }}
      control={<Checkbox id={idModalChk} color="primary" value={"xxx"} />}
      checked={chkConfirm}
      label={labelChk}
      onChange={(e: object) => setChkConfirm(!chkConfirm)}
    />
  );

  return (
    <Dialog
      classes={{ paper: wide ? classes.alertWide : classes.alert }}
      disableBackdropClick={nobackdrop}
      open={true}
      fullScreen={wide && useMediaQuery(useTheme().breakpoints.down("xs"))}
      onClose={onExit}>
      <DialogTitle
        disableTypography
        className={classNames({
          [classes[`alert_${color}`]]: color,
        })}>
        <h4>{title}</h4>
        {closeButton(classes, () => onExit())}
      </DialogTitle>
      <div className={classes.alertBody}>
        <DialogContent
          id="classic-modal-slide-description"
          className={classes.modalBody}>
          {children || <p>{body || ""}</p>}
          {textInputRender}
          <p>{chkboxRender}</p>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          {buttonsRender}
        </DialogActions>
      </div>
    </Dialog>
  );
};

export const Modal = React.memo(withStyles(styles)(ModalBase));
